import { useState, useRef, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Loader2, Info } from "lucide-react";
import { useSnapshot } from "valtio";
import { store } from "@/store";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface EditableCellProps {
  value: string | number | boolean | string[];
  onSave: (value: string | number | boolean | string[]) => void;
  className?: string;
  companyId?: string;
  columnName?: string;
  onShowBasis?: () => void;
}

export function EditableCell({ value, onSave, className, companyId, columnName, onShowBasis }: EditableCellProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(Array.isArray(value) ? value.join(', ') : String(value));
  const [isHovered, setIsHovered] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const snap = useSnapshot(store);

  const isEnriching = companyId && columnName ? snap.enrichmentState[companyId]?.[columnName] : false;
  const hasBasis = companyId && columnName ? !!snap.taskBasisData[companyId]?.[columnName] : false;

  // Extract confidence from the first basis item
  const confidence = companyId && columnName
    ? (snap.taskBasisData[companyId]?.[columnName]?.basis?.[0] as any)?.confidence as "high" | "medium" | "low" | undefined
    : undefined;


  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleDoubleClick = () => {
    setIsEditing(true);
    setEditValue(Array.isArray(value) ? value.join(', ') : String(value));
  };

  const handleSave = () => {
    setIsEditing(false);
    const trimmedValue = editValue.trim();

    // Try to preserve the original type
    if (Array.isArray(value)) {
      // Convert comma-separated string back to array
      const arrayValue = trimmedValue.split(',').map(item => item.trim()).filter(item => item.length > 0);
      onSave(arrayValue);
    } else if (typeof value === 'number') {
      const numValue = Number(trimmedValue);
      onSave(isNaN(numValue) ? trimmedValue : numValue);
    } else if (typeof value === 'boolean') {
      onSave(trimmedValue.toLowerCase() === 'true');
    } else {
      onSave(trimmedValue);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      setIsEditing(false);
      setEditValue(Array.isArray(value) ? value.join(', ') : String(value));
    }
  };

  if (isEditing) {
    return (
      <Input
        ref={inputRef}
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        className="w-full h-full text-sm border-0 rounded-none shadow-none focus:ring-0 focus:ring-offset-0 px-4 py-0"
      />
    );
  }

  const getConfidenceColor = (conf?: "high" | "medium" | "low") => {
    switch (conf) {
      case "high":
        return "bg-green-500";
      case "medium":
        return "bg-yellow-500";
      case "low":
        return "bg-red-500";
      default:
        return null;
    }
  };

  const getConfidenceLabel = (conf?: "high" | "medium" | "low") => {
    switch (conf) {
      case "high":
        return "High confidence";
      case "medium":
        return "Medium confidence";
      case "low":
        return "Low confidence";
      default:
        return "";
    }
  };

  const confidenceColor = getConfidenceColor(confidence);
  const confidenceLabel = getConfidenceLabel(confidence);

  return (
    <div
      onDoubleClick={isEnriching ? undefined : handleDoubleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`${isEnriching ? 'cursor-default' : 'cursor-pointer hover:bg-muted/50'} w-full h-full flex items-center justify-between gap-2 ${className || ''}`}
      title={isEnriching ? "Enriching..." : "Double-click to edit"}
    >
      <div className="flex-1 break-words whitespace-normal flex items-center gap-2">
        {confidenceColor && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                className={`w-3 h-3 rounded-full shrink-0 cursor-help ${confidenceColor}`}
              />
            </TooltipTrigger>
            <TooltipContent>
              <p>{confidenceLabel}</p>
            </TooltipContent>
          </Tooltip>
        )}
        {isEnriching ? (
          <div className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            <span className="text-muted-foreground">Enriching...</span>
          </div>
        ) : (
          <span>{Array.isArray(value) ? value.join(', ') : String(value)}</span>
        )}
      </div>
      {hasBasis && onShowBasis && (
        <Button
          variant="ghost"
          size="sm"
          className={`h-6 w-6 p-0 shrink-0 transition-opacity ${isHovered ? 'opacity-100' : 'opacity-0'}`}
          onClick={(e) => {
            e.stopPropagation();
            onShowBasis();
          }}
          title="Show research basis"
        >
          <Info className="h-3.5 w-3.5 text-blue-600" />
        </Button>
      )}
    </div>
  );
}